import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import mixpanel from "mixpanel-browser";
import { TextField, PasswordField, Button, CheckboxField } from "@aws-amplify/ui-react";
import { Disclaimer, DisclaimerWrapper, ErrorContainer, ErrorText, Icon } from "../../../components/GeneralStyles/GeneralStyles";
import { useFade } from "../../../hooks/useFade";
import Attention from "../Assets/Attention.svg";
import PasswordValidator from "../../../utils/PasswordValidator/PasswordValidator";
import FullNameContainer from "../Components/FullNameContainer";
import { checkStatesAndPhoneNumber } from "../Signup.service";
import { analyticsService } from "../../../services/AnalyticsService";
import { getErrorTextComponent } from "../../../utils/ErrorMapping";
import { useSignup } from "../../../contexts/SignupContext";
import { determineUserType } from "../../../utils/UserType";

const StepTwo = ({ step, handleChange, handleChecked, recaptchaRef }: any) => {
  const [isCompanyFocused, setIsCompanyFocused] = useState(false);
  const {
    setEmailError,
    isNotPartOfCompany,
    setCompanyError,
    setUrl,
    given_name,
    family_name,
    username,
    password,
    checked,
    companyName,
    recaptchaToken,
    setIncomplete,
    emailCandidate,
    setIsNotPartOfCompany,
    emailError,
    companyError,
    invalidPassword,
    setInvalidPassword,
    disclaimerError,
    cognitoError,
    incomplete,
    isDisabled,
  } = useSignup();
  const userType = determineUserType();

  const validateEmail = (e: any) => {
    if (
      e.currentTarget.value.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )
    ) {
      setEmailError(false);
    } else if (!e.currentTarget.value) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  };

  const validateCompanyName = (e?: React.FocusEvent<HTMLInputElement>) => {
    if (isNotPartOfCompany || !e) {
      setCompanyError(false);
      return;
    }

    const nonLetterOrDigitRegex = /[^a-zA-Z0-9]/g;

    // Remove any non-English letters or digits from the string
    const cleanedStr = e.currentTarget.value.replace(nonLetterOrDigitRegex, "");

    // Check if the cleaned string has at least 2 English letters or digits
    const hasValidChars = /[a-zA-Z0-9]{3,}/.test(cleanedStr);

    if (hasValidChars) {
      setCompanyError(false);
    } else {
      setCompanyError(true);
    }
  };

  useEffect(() => {
    setIsVisible(true);
    setUrl(window.location.href);
    if (userType === "selfService") {
      analyticsService.track("SES - Signup Page View", {}, () => {});
      mixpanel.track_links("#SupportLink", "SES - Support Link Clicked");
    }
  }, []);

  useEffect(() => {
    if (userType === "selfService") {
      checkStatesAndPhoneNumber([given_name, family_name, username, password, checked, companyName], null, userType, step, setIncomplete);
    } else {
      checkStatesAndPhoneNumber([username, password, checked], null, "candidate", step, setIncomplete);
    }
  }, [given_name, family_name, username, password, checked, companyName]);

  const [isVisible, setIsVisible, fadeProps] = useFade(false);
  return (
    <div {...fadeProps}>
      {userType === "selfService" ? <FullNameContainer handleChange={handleChange} given_name={given_name} family_name={family_name} /> : null}
      <TextField
        label="Email"
        id="email"
        aria-label="Enter your email address"
        onChange={(e: any) => handleChange("email", e)}
        defaultValue={username}
        isDisabled={emailCandidate ? true : false}
        isRequired={true}
        onBlur={validateEmail}
      />
      {userType === "selfService" ? (
        <>
          <TextField
            label="Company"
            id="companyName"
            aria-label="Enter your company name"
            onFocus={() => setIsCompanyFocused(true)}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              setIsCompanyFocused(false);
              validateCompanyName(e);
            }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!isNotPartOfCompany) {
                handleChange("companyName", e);
              }
            }}
            value={isNotPartOfCompany ? "" : companyName}
            isRequired={!isNotPartOfCompany}
            disabled={isNotPartOfCompany}
            className={`${isCompanyFocused ? "company-focused" : ""} ${isNotPartOfCompany ? "label-gray" : ""}`}
            style={{ marginBottom: "24px" }}
          />

          <CheckboxField
            className={`company-checkbox ${isCompanyFocused ? "checkbox-focused" : ""}`}
            name="isNotPartOfCompany"
            id="isNotPartOfCompany"
            value="yes"
            checked={isNotPartOfCompany}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const isChecked = e.target.checked;
              setIsNotPartOfCompany(isChecked);
              if (isChecked) {
                handleChange("companyName", { currentTarget: { value: given_name + " " + family_name } });
              } else {
                handleChange("companyName", { currentTarget: { value: "" } }); // Clear the input when unchecked
              }
              setCompanyError(false);
            }}
            label="I'm not a part of a company"
            crossOrigin={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        </>
      ) : null}
      {emailError || companyError ? (
        <ErrorContainer aria-live="polite">
          <Icon src={Attention} marginTop={"14px"} alt="attention icon" />
          <ErrorText>Please enter valid {emailError ? "email address" : "company name"} </ErrorText>
        </ErrorContainer>
      ) : null}
      <PasswordField
        className="password"
        id="password"
        label="Password"
        aria-label="Choose a password"
        placeholder="Choose password"
        autoComplete="new-password"
        isRequired={true}
        onChange={(e) => handleChange("password", e)}
        defaultValue={password}
        hasError={invalidPassword}
        style={invalidPassword ? { border: "1px solid #E61F26" } : undefined}
        crossOrigin={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      />
      <PasswordValidator password={password} setInvalidPassword={setInvalidPassword} />
      <DisclaimerWrapper>
        <CheckboxField
          name="disclaimer"
          id="disclaimer"
          aria-label="checkbox"
          type="checkbox"
          value="yes"
          checked={checked}
          onChange={handleChecked}
          hasError={disclaimerError}
          label=""
          crossOrigin={undefined}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
        {userType === "candidate" ? (
          <Disclaimer marginBottom={disclaimerError ? "0px" : "32px"}>
            I have read and accepted the{" "}
            <a href="https://verbit.ai/freelancer-tou/" target="_blank" rel="noreferrer">
              terms and conditions
            </a>
            ,{" "}
            <a href="https://verbit.ai/privacy-policy/" target="_blank" rel="noreferrer">
              privacy policy
            </a>
            ,{" "}
            <a href="https://verbit.ai/freelancer-dpa/" target="_blank" rel="noreferrer">
              data processing agreement
            </a>
            , and
            <a href="https://verbit.ai/freelancer-baa/" target="_blank" rel="noreferrer">
              {" "}
              business associate agreement
            </a>
          </Disclaimer>
        ) : (
          <Disclaimer marginBottom={disclaimerError ? "0px" : "32px"}>
            By registering for an account you agree to our{" "}
            <a href="https://verbit.ai/terms-and-conditions/" target="_blank" rel="noreferrer">
              Terms of Use.
            </a>
          </Disclaimer>
        )}
      </DisclaimerWrapper>
      {disclaimerError ? (
        <ErrorContainer margin={"0px 0 21px 0"} aria-live="polite">
          <ErrorText>This is a required field</ErrorText>
        </ErrorContainer>
      ) : null}
      <ReCAPTCHA
        ref={recaptchaRef}
        className="captchaWrapper"
        sitekey="6Les4oQqAAAAACoToV1rUFm42iMA46prk-Uh3aay"
        size="invisible"
      />
      {cognitoError ? <ErrorContainer aria-live="polite">{getErrorTextComponent(cognitoError)}</ErrorContainer> : null}
      <Button
        isFullWidth={true}
        type="submit"
        isDisabled={incomplete || isDisabled}
        aria-label="Sign Up"
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
      >
        Sign Up
      </Button>
    </div>
  );
};

export default StepTwo;
